import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SalePaymentMethodModel } from '@fleet/model';
import { DateTime } from 'luxon';

export function optionalValidator(
  validators?: (ValidatorFn | null | undefined)[]
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const error =
      control.value && control.value !== ''
        ? Validators.compose(validators)(control)
        : null;
    return error;
  };
}

export function minDate(minDate: DateTime): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dateToValidate = DateTime.fromISO(control.value);
    return minDate <= dateToValidate
      ? null
      : {
          minDate:
            'Please ensure date is on or after ' +
            minDate.toFormat('dd/MM/yyyy'),
        };
  };
}

export function maxDate(maxDate: DateTime): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dateToValidate = DateTime.fromISO(control.value);

    return maxDate >= dateToValidate
      ? null
      : {
          maxDate:
            'Please ensure date is on or before ' +
            maxDate.toFormat('dd/MM/yyyy'),
        };
  };
}
export function dateRangeBeforeAfterValidator(
  startDateTerm: string,
  endDateTerm: string,
  ignoreTime?: boolean
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    let fromDate = control.get(startDateTerm).value;
    let toDate = control.get(endDateTerm).value;
    if (typeof fromDate === 'string') {
      fromDate = DateTime.fromISO(fromDate);
    }
    if (typeof toDate === 'string') {
      toDate = DateTime.fromISO(toDate);
    }

    //TO DATE COULD BE NULL - IT MAY BE IN AN OPTIONAL STATE.

    if (fromDate && toDate) {
      if (fromDate.isValid && toDate.isValid) {
        if (
          !ignoreTime &&
          fromDate.hasSame(toDate, 'day') &&
          (fromDate.hour > toDate.hour ||
            (fromDate.hour === toDate.hour && fromDate.minute >= toDate.minute))
        ) {
          return {
            timeNotFuture: true,
            dateRangeBeforeAfter: true,
          };

          //So lets purposely only set the time to need to be
        } else if (fromDate.equals(toDate) || fromDate > toDate) {
          // its the same date, but the time is okay

          return {
            dateNotFuture: true,
            dateRangeBeforeAfter: true,
          };
        }
      } else {
        return {
          dateRangeBeforeAfter: true,
          timeNotFuture: true,
        };
      }

      if (fromDate.toMillis() > toDate.toMillis()) {
        return {
          dateNotFuture: true,
          dateRangeBeforeAfter: true,
        };
      }
    } else if (fromDate && fromDate.invalid) {
      return {
        dateNotFuture: true,
        dateRangeBeforeAfter: true,
      };
    } else if (toDate && toDate.invalid) {
      return {
        dateNotFuture: true,
        dateRangeBeforeAfter: true,
      };
    }

    return null;
  };
}

export function bsbValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null; // allow empty values
    }

    const bsbRegex = /^(\d{3})-?(\d{3})$/;
    if (!bsbRegex.test(value)) {
      return { invalidBsb: true };
    }

    return null;
  };
}

export function latLngValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.value && control.value.latitude && control.value.longitude) {
      return null;
    } else {
      return { placeInvalid: 'Please select a valid address' };
    }
  };
}
export function addressValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    if (!value) {
      return null; // If there's no value, it's considered valid.
    }
    if (
      value.structuredAddress?.placeName ||
      value.structuredAddress?.streetName ||
      value.organisationGroupLocationId
    ) {
      return null; // If it has a structuredAddress or both locationId and organisationGroupLocationId, it's valid.
    }
    // If none of the above conditions are met, the address is invalid.
    return { placeInvalid: 'Please select a valid address' };
  };
}

/**
 * An address a ride can actually be planned to. Accepts an address that is already stored (it carries a `locationId`,
 * so the server already holds its position), or a newly picked one that states a street or place name *and* both
 * coordinates. The address picker's manual-entry fallback emits a structured address with no coordinates, which the
 * API refuses outright, so coordinates are required alongside a typed address rather than discovered on save.
 * Unlike `addressValidator`, an empty value is invalid: pair it with `Validators.required` only for the message.
 */
export function plannableAddressValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return { addressNotPlannable: true };
    }
    if (value.locationId) {
      return null;
    }
    const named =
      value.structuredAddress?.streetName || value.structuredAddress?.placeName;
    return named && value.latitude && value.longitude
      ? null
      : { addressNotPlannable: true };
  };
}

export function postalValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.value && control.value.structuredAddress) {
      return null;
    } else {
      return { placeInvalid: 'Please select a valid address' };
    }
  };
}

/**
 * Requires a date value on `fleet-date-with-time-control` without implying time is required.
 * Use instead of `Validators.required` when the host must be filled but time may stay empty.
 */
export function dateWithTimeDateRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') {
      return { required: true };
    }
    if (DateTime.isDateTime(value)) {
      return value.isValid ? null : { required: true };
    }
    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value);
      return parsed.isValid ? null : { required: true };
    }
    return { required: true };
  };
}

export function hasRequiredValidator(control: AbstractControl): boolean {
  if (!control.validator) return false;

  const validationResult = Object.assign(
    {},
    control.validator({} as AbstractControl)
  );
  return validationResult && validationResult.required;
}

export function jobPaymentMethodValidator(
  groupRequiredOnPayDriver: boolean
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as SalePaymentMethodModel;
    if (value?.type === 'OTHER' && groupRequiredOnPayDriver) {
      return value.organisationGroupId
        ? null
        : {
            groupRequiredOnPayDriver:
              'Please ensure you set a group on pay driver',
          };
    } else if (value?.type === 'OTHER' || value?.paymentMethodId) {
      return null; // The control is valid if it has type 'OTHER' or a paymentMethodId
    } else if (value?.organisationGroupId) {
      let errorMessage =
        'This organisation group payment option cannot be used.';
      if (value.profileId) {
        errorMessage +=
          ' Please set a default payment method on your organisation profile.';
      } else {
        errorMessage +=
          ' Please choose a group that has a payment method assigned. ';
      }
      return {
        organisationGroupPayment: errorMessage,
      };
    }
    return null;

    // /return { invalidJobPaymentMethod: 'The payment method is not valid.' }; // Return an error object if the conditions are not met
  };
}

export function noLeadingSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null; // Allow empty values - use required validator separately
    }
    if (typeof value === 'string') {
      // Fail if value is only whitespace (to prevent bypassing required validator)
      if (value.trim().length === 0) {
        return { noLeadingSpaces: 'Field cannot be only spaces' };
      }
      // Fail if value starts with whitespace
      if (/^\s/.test(value)) {
        return { noLeadingSpaces: 'Field cannot start with spaces' };
      }
    }
    return null;
  };
}

function timeToHourMinute(
  inputTime: string | DateTime | null | undefined
): { hour: number; minute: number } | null {
  if (inputTime == null) {
    return null;
  }
  if (inputTime instanceof DateTime) {
    return inputTime.isValid
      ? { hour: inputTime.hour, minute: inputTime.minute }
      : null;
  }
  if (typeof inputTime === 'string') {
    const parts = inputTime.split(':');
    if (parts.length < 2) {
      return null;
    }
    return {
      hour: parseInt(parts[0], 10),
      minute: parseInt(parts[1], 10),
    };
  }
  return null;
}

export const minDateValidator = (
  minDate: DateTime,
  excludeTime?: boolean,
  customDateErrorMessage?: string,
  customTimeErrorMessage?: string
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const controlValue = control.value;
    if (!controlValue) {
      return null; // If no value is present, do not perform validation for minDate
    }

    let combinedDateTime: DateTime;

    if (controlValue instanceof DateTime) {
      combinedDateTime = controlValue;
    } else {
      if (!controlValue.date && !controlValue.time) {
        return null; // If no value is present, do not perform validation for minDate
      } else if (!controlValue.date && controlValue.time) {
        return {
          dateNotFuture: true,
          customDateErrorMessage: customDateErrorMessage,
        };
      }

      const inputDate =
        controlValue.date instanceof DateTime
          ? controlValue.date
          : DateTime.fromISO(controlValue.date);
      const timeParts = timeToHourMinute(controlValue.time);

      if (!timeParts && !excludeTime) {
        return {
          timeNotFuture: true,
          customTimeErrorMessage: customTimeErrorMessage,
        };
      }
      if (!inputDate.isValid) {
        return null;
      }
      combinedDateTime = inputDate.set({
        hour: excludeTime ? 0 : timeParts.hour,
        minute: excludeTime ? 0 : timeParts.minute,
      });
    }

    const zone = minDate.zone;
    const min = minDate.setZone(zone).startOf('minute');
    const combined = combinedDateTime.setZone(zone).startOf('minute');

    if (excludeTime) {
      if (combined.startOf('day') < min.startOf('day')) {
        return {
          dateNotFuture: true,
          customDateErrorMessage: customDateErrorMessage,
        };
      }
      return null;
    }

    if (
      min.hasSame(combined, 'day') &&
      min.hour > combined.hour
    ) {
      // It is the SAME date and the from TIMES are greater..
      return {
        timeNotFuture: true,
        customTimeErrorMessage: customTimeErrorMessage,
      };
    } else if (
      min.hasSame(combined, 'day') &&
      min.hour === combined.hour &&
      min.minute >= combined.minute
    ) {
      return {
        timeNotFuture: true,
        customTimeErrorMessage: customTimeErrorMessage,
      };
    } else if (min.toMillis() > combined.toMillis()) {
      if (combined.startOf('day') < min.startOf('day')) {
        return {
          dateNotFuture: true,
          customDateErrorMessage: customDateErrorMessage,
        };
      }
      return {
        timeNotFuture: true,
        customTimeErrorMessage: customTimeErrorMessage,
      };
    } else {
      return null; // If minDate and time are not in the past, validation passes
    }
  };
};
export function intervalValidator(step: number): ValidatorFn {
  const interval = step / 60;
  if (60 % interval !== 0) {
    throw new Error(
      'Step must result in an interval that evenly divides 60 minutes.'
    );
  }
  const divider = 60 / interval;
  const allowedMinutes: number[] = [];
  for (let i = 0; i < divider; i++) {
    allowedMinutes.push(i * interval);
  }
  return (control: AbstractControl) => {
    if (!control.value) return null;
    const [hours, minutes] = control.value.split(':').map(Number);
    if (!allowedMinutes.includes(minutes)) {
      return { invalidInterval: true };
    }
    return null;
  };
}

/**
 * Optional 4-digit calendar year (e.g. vehicle year of manufacture).
 * Empty values are valid; use Validators.required if the field is mandatory.
 */
export function yearValidator(minYear = 1900, maxYear = 2100): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const str = String(value);
    if (!/^\d{4}$/.test(str)) {
      return { invalidYear: 'Year must be a 4-digit number' };
    }

    const year = parseInt(str, 10);
    if (year < minYear || year > maxYear) {
      return {
        invalidYear: `Year must be between ${minYear} and ${maxYear}`,
      };
    }

    return null;
  };
}
